// ═══════════════════════════════════════════════════════════════════════════
//  ExecutionConsolePanel — the log stream at the bottom-right of the page.
//
//  Lifted out of LiveTrading.tsx (God 2026-08-26, phase 3). Unlike phases 1 and
//  2, this block lived INSIDE the component and read its state, so it could not
//  be moved verbatim — it takes props instead. The JSX itself is unchanged; only
//  the six values it used to close over now arrive through the interface below.
//
//  Six props is the whole surface, which is why this section went first: it
//  proves the props-and-icons pattern end to end on a block small enough to
//  check by eye, before the same pattern is applied to the strategy card list,
//  which needs roughly twenty-five.
//
//  Both lanes render this: DUMABATI and KALI are the same page component, so a
//  change here shows up in both. That is the point of the shared page.
// ═══════════════════════════════════════════════════════════════════════════
import React from 'react';
import { RefreshCw, Terminal } from 'lucide-react';
import { Log } from './foundation';

export interface ExecutionConsolePanelProps {
  /** Newest-last log buffer owned by the page. */
  logs: Log[];
  /** Current level filter — 'ALL' shows everything. */
  logFilter: string;
  setLogFilter: (v: string) => void;
  /** KEEP_ALIVE pings are hidden by default: they are heartbeat noise and would
   *  otherwise bury the signal and order lines this console exists to show. */
  showPings: boolean;
  setShowPings: React.Dispatch<React.SetStateAction<boolean>>;
  /** Manual refresh — the same fetch the page runs on its poll. */
  fetchBackendData: () => void;
  /** Scroll container, owned by the page so it can pin to the newest line. */
  consoleRef: React.RefObject<HTMLDivElement>;
}

export const ExecutionConsolePanel: React.FC<ExecutionConsolePanelProps> = ({
  logs, logFilter, setLogFilter, showPings, setShowPings, fetchBackendData, consoleRef,
}) => (
  <>
    <div style={{padding:'9px 14px',borderBottom:'1px solid #1e293b',fontSize:10,fontWeight:800,color:'#94a3b8',textTransform:'uppercase',display:'flex',justifyContent:'space-between',alignItems:'center'}}>
      <div style={{display:'flex',alignItems:'center',gap:6}}><Terminal size={13}/> Execution Console</div>
      <div style={{display:'flex',gap:6,alignItems:'center'}}>
        <button onClick={()=>setShowPings(p=>!p)} className="lt-btn"
          title={showPings ? 'KEEP_ALIVE pings dikh rahe — click to hide' : 'KEEP_ALIVE pings hidden — click to show'}
          style={{padding:'3px 7px',background:showPings?'rgba(168,85,247,.15)':'#1e293b',color:showPings?'#a855f7':'#475569',fontSize:8,fontWeight:800}}>
          🏓
        </button>
        <button onClick={fetchBackendData} className="lt-btn" style={{padding:'4px 8px',background:'#1e293b',color:'#10b981',fontSize:8}} title="Refresh">
          <RefreshCw size={10}/>
        </button>
        <select value={logFilter} onChange={e=>setLogFilter(e.target.value)} className="lt-inp" style={{width:'auto',padding:'4px 8px',fontSize:9}}>
          <option value="ALL">ALL</option><option value="INFO">INFO</option>
          <option value="SUCCESS">SUCCESS</option><option value="WARNING">WARN</option>
          <option value="ERROR">ERROR</option><option value="TRADE">TRADE</option>
        </select>
      </div>
    </div>
    <div ref={consoleRef} className="lt-scroll" style={{flex:1,overflowY:'auto',padding:'8px 12px',fontFamily:'monospace'}}>
      {(() => {
        const visible = logs.filter(l =>
          (logFilter === 'ALL' || l.type === logFilter) &&
          (showPings || l.system !== 'KEEP_ALIVE'));
        if (visible.length === 0) return (
          <div style={{fontSize:9,color:'#475569',padding:'6px 0'}}>
            koi events nahi{!showPings ? ' (🏓 pings hidden)' : ''} — signals/orders aate hi yahan dikhenge
          </div>
        );
        return visible.map(l => (
          <div key={l.id} style={{fontSize:9,marginBottom:4,display:'flex',gap:6}}>
            <span style={{color:'#4b5563'}}>[{l.time}]</span>
            <span style={{color:l.type==='ERROR'?'#f87171':l.type==='SUCCESS'?'#10b981':l.type==='WARNING'?'#fbbf24':l.type==='TRADE'?'#60a5fa':'#a855f7',fontWeight:700}}>{l.type}</span>
            <span style={{color:'#6b7280'}}>{l.system}:</span>
            <span style={{color:'#9ca3af'}}>{l.message}</span>
          </div>
        ));
      })()}
    </div>
  </>
);

export default ExecutionConsolePanel;
